import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import api from "../../lib/api";
import { Loading, EmptyState } from "../../components/States";
import { CHead, CCard, Pill } from "../../components/customer/ui";
import { toast } from "sonner";
import { ShieldCheck, Plus, MessageSquare } from "lucide-react";

export default function CustomerWarranty() {
  const warranty = useFetch("/warranty");
  const messages = useFetch("/messages");
  const [title, setTitle] = useState("");
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post("/warranty", { title });
      toast.success("Warranty ticket opened. We'll be in touch.");
      setTitle(""); setOpen(false);
      warranty.refetch();
    } catch { toast.error("Could not open ticket"); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { body: msg });
      toast.success("Message sent to Hardwire");
      setMsg("");
      messages.refetch();
    } catch { toast.error("Could not send"); }
  };

  if (warranty.loading || messages.loading) return <Loading />;

  return (
    <div className="space-y-6">
      <CHead
        title="Warranty"
        subtitle="Ask about coverage under your written estimate or contract."
        action={
          <button onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-5 py-3 font-semibold text-white hover:bg-[#0A84FF]" data-testid="open-ticket-btn">
            <Plus size={18} /> New Ticket
          </button>
        }
      />

      {open && (
        <CCard className="p-5">
          <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="warranty-form">
            <input className="flex-1 rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Describe the issue…" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="warranty-title" />
            <button className="rounded-xl bg-[#1286F2] px-6 py-3 font-semibold text-white hover:bg-[#0A84FF]" data-testid="warranty-submit">Open</button>
          </form>
        </CCard>
      )}

      <div className="space-y-3">
        {(warranty.data || []).length === 0 && <EmptyState title="No warranty tickets" subtitle="Your installed work is covered." icon={ShieldCheck} />}
        {(warranty.data || []).map((w) => (
          <CCard key={w.id} className="p-5" data-testid={`c-warranty-${w.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-[#1286F2]">{w.ticket}</div>
                <div className="mt-1 font-semibold text-slate-900">{w.title}</div>
              </div>
              <Pill status={w.status} />
            </div>
          </CCard>
        ))}
      </div>

      <CCard className="p-6">
        <div className="mb-4 flex items-center gap-2"><MessageSquare size={18} className="text-[#1286F2]" /><span className="text-xl font-bold text-slate-900">Message Hardwire</span></div>
        <div className="mb-4 space-y-2">
          {(messages.data || []).map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "Hardwire" ? "bg-slate-100 text-slate-700" : "ml-auto bg-[#1286F2] text-white"}`}>
              {m.body}
            </div>
          ))}
        </div>
        <form onSubmit={sendMsg} className="flex gap-2">
          <input className="flex-1 rounded-xl border border-slate-300 p-3 focus:border-[#1286F2] focus:outline-none" placeholder="Type a message…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input" />
          <button className="rounded-xl bg-[#1286F2] px-6 font-semibold text-white hover:bg-[#0A84FF]" data-testid="message-send">Send</button>
        </form>
      </CCard>
    </div>
  );
}
